import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Typography, TextField,
  InputAdornment, Skeleton, useTheme, alpha, IconButton,
  Button, Slide, CircularProgress, Divider
} from '@mui/material';
import {
  Search, Clear, MeetingRoom, PeopleOutline, CheckCircle,
  EditOutlined, Save, Undo
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { teachingLoadsApi, ClassTeacherRow } from '../../services/sessions/teachingLoadsApi';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useDebounce } from '../../hooks/useDebounce';
import TeacherPickerSelect, { TeacherOption } from './TeacherPickerSelect';
import SectionChip from './SectionChip';
import EmptyState from '../common/EmptyState';

interface Props {
  sessionId: string;
  teachers: TeacherOption[];
}

const DEFAULT_ROWS_PER_PAGE = 25;

// Key used to identify a slot in both the rows array and the pending-changes
// map. Mirrors the backend's two storage paths: sectionInstanceId for
// sectioned classes, `class:<classId>` for sectionless classes.
const slotKey = (row: { sectionInstanceId: string | null; classId: string }) =>
  row.sectionInstanceId ?? `class:${row.classId}`;

const ClassTeachersTab: React.FC<Props> = ({ sessionId, teachers }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const writable = canWrite('teaching_loads');

  const [rows, setRows] = useState<ClassTeacherRow[]>([]);
  const [loading, setLoading] = useState(true);
  // Map<slotKey, TeacherOption | null> — null means "unassign on save".
  // Absent key means the row hasn't been touched.
  const [pending, setPending] = useState<Map<string, TeacherOption | null>>(new Map());
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 250);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await teachingLoadsApi.listClassTeachers(sessionId);
      setRows(data);
      setPending(new Map());
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not load class teachers', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [sessionId, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [debouncedSearch]);

  // Resolve the value to render for a row — pending edit wins over server state.
  const effectiveTeacherFor = useCallback(
    (row: ClassTeacherRow): { _id: string; name: string } | null => {
      const k = slotKey(row);
      if (pending.has(k)) return pending.get(k) ?? null;
      return row.classTeacher;
    },
    [pending]
  );

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => {
      const t = effectiveTeacherFor(r);
      return (
        r.className.toLowerCase().includes(q) ||
        (r.sectionName?.toLowerCase().includes(q) ?? false) ||
        (t?.name.toLowerCase().includes(q) ?? false) ||
        (r.room?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [rows, debouncedSearch, effectiveTeacherFor]);

  const paged = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  const stats = useMemo(() => {
    const total = rows.length;
    // Stats reflect the post-save state so the user sees the impact of their
    // edits before they commit.
    const assigned = rows.filter(r => effectiveTeacherFor(r) !== null).length;
    return { total, assigned, unassigned: total - assigned };
  }, [rows, effectiveTeacherFor]);

  const handlePick = (row: ClassTeacherRow, teacher: TeacherOption | null) => {
    if (!writable) return;
    const k = slotKey(row);
    const original = row.classTeacher;
    const sameAsOriginal =
      (original?._id ?? null) === (teacher?._id ?? null);

    setPending(prev => {
      const next = new Map(prev);
      if (sameAsOriginal) {
        // Reverted to the saved value — clear the pending entry so the row
        // no longer reads as dirty.
        next.delete(k);
      } else {
        next.set(k, teacher);
      }
      return next;
    });
  };

  const handleDiscard = () => {
    setPending(new Map());
  };

  const handleSave = async () => {
    if (pending.size === 0) return;
    setSaving(true);

    // Snapshot to avoid races if pending changes while we're saving.
    const entries = Array.from(pending.entries());
    const rowsByKey = new Map(rows.map(r => [slotKey(r), r]));

    const results = await Promise.allSettled(
      entries.map(async ([key, teacher]) => {
        const row = rowsByKey.get(key);
        if (!row) throw new Error(`Row no longer present: ${key}`);
        const slot = row.sectionInstanceId
          ? { sectionInstanceId: row.sectionInstanceId }
          : { sectionInstanceId: null as null, classId: row.classId, sessionId };
        const updated = await teachingLoadsApi.setClassTeacher(slot, teacher?._id ?? null);
        return { key, updated };
      })
    );

    const succeeded: Array<{ key: string; updated: ClassTeacherRow }> = [];
    const failed: string[] = [];
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') succeeded.push(r.value);
      else failed.push(entries[i][0]);
    });

    if (succeeded.length > 0) {
      // Merge the successful server responses back into rows.
      setRows(prev => prev.map(r => {
        const hit = succeeded.find(s => s.key === slotKey(r));
        return hit ? hit.updated : r;
      }));
    }

    // Drop succeeded keys from pending — keep the failed ones so the user can
    // retry without losing their edits.
    setPending(prev => {
      const next = new Map(prev);
      for (const s of succeeded) next.delete(s.key);
      return next;
    });

    setSaving(false);

    if (failed.length === 0) {
      enqueueSnackbar(
        succeeded.length === 1
          ? 'Class teacher updated'
          : `${succeeded.length} class teacher assignments updated`,
        { variant: 'success' }
      );
    } else if (succeeded.length === 0) {
      enqueueSnackbar(
        failed.length === 1
          ? 'Could not save change'
          : `Could not save ${failed.length} changes`,
        { variant: 'error' }
      );
    } else {
      enqueueSnackbar(
        `Saved ${succeeded.length}, ${failed.length} failed — failed rows are kept for retry`,
        { variant: 'warning' }
      );
    }
  };

  const pendingCount = pending.size;

  return (
    <Box sx={{ position: 'relative', pb: pendingCount > 0 ? 8 : 0 }}>
      {/* Stat banner */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          flexWrap: 'wrap',
          bgcolor: alpha(theme.palette.primary.main, 0.04)
        }}
      >
        <Stat icon={<PeopleOutline color="primary" />} label="Slots" value={stats.total} />
        <Stat icon={<CheckCircle color="success" />}    label="With class teacher" value={stats.assigned} accent="success.main" />
        <Stat icon={<PeopleOutline color="warning" />}  label="Unassigned" value={stats.unassigned} accent={stats.unassigned > 0 ? 'warning.main' : undefined} />
        {pendingCount > 0 && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Stack direction="row" spacing={1} alignItems="center">
              <EditOutlined sx={{ color: 'warning.main', fontSize: 18 }} />
              <Typography variant="caption" sx={{ color: 'warning.dark', fontWeight: 600 }}>
                {pendingCount} unsaved change{pendingCount === 1 ? '' : 's'}
              </Typography>
            </Stack>
          </>
        )}
      </Paper>

      {/* Filter row */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search by class, section, teacher or room"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 320 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.25 }} aria-label="Clear search">
                      <Clear sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined
              }
            }}
          />
        </Stack>
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.025) }}>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Class</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Section</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: 280 }}>Class teacher</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Room</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }} align="right">Capacity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 5 }).map((_, j) => (
                      <TableCell key={j}><Skeleton variant="text" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paged.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 6 }}>
                    <EmptyState
                      icon={<PeopleOutline sx={{ fontSize: 36 }} />}
                      title={rows.length === 0 ? 'No sections in this session' : 'No matching sections'}
                      description={rows.length === 0
                        ? 'Create sections in the Sections tab to assign class teachers here.'
                        : 'Try a different search term, or clear the filter.'}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                paged.map(row => {
                  const rowKey = row.sectionInstanceId ?? `class-${row.classId}`;
                  const isSectionless = row.isClassWithoutSections === true;
                  const k = slotKey(row);
                  const isPending = pending.has(k);
                  const effective = effectiveTeacherFor(row);
                  return (
                    <TableRow
                      key={rowKey}
                      hover
                      sx={{
                        transition: 'background-color 120ms ease',
                        ...(isPending && {
                          bgcolor: alpha(theme.palette.warning.main, 0.04)
                        }),
                        '&:hover': {
                          bgcolor: isPending
                            ? alpha(theme.palette.warning.main, 0.08)
                            : alpha(theme.palette.primary.main, 0.03)
                        }
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{row.className}</Typography>
                      </TableCell>
                      <TableCell>
                        {isSectionless ? (
                          <SectionChip
                            name="Whole class"
                            isVirtual
                            tooltip="This class has no sections in this session — the homeroom applies to the whole class."
                          />
                        ) : (
                          <SectionChip name={row.sectionName ?? ''} />
                        )}
                      </TableCell>
                      <TableCell>
                        <TeacherPickerSelect
                          teachers={teachers}
                          value={effective}
                          onChange={(t) => handlePick(row, t)}
                          disabled={!writable || saving}
                          pending={isPending}
                        />
                      </TableCell>
                      <TableCell>
                        {row.room ? (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <MeetingRoom sx={{ fontSize: 14, color: 'text.disabled' }} />
                            <Typography variant="body2">{row.room}</Typography>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {row.capacity ?? <Typography component="span" variant="caption" color="text.disabled">—</Typography>}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Sticky save bar — slides up once the page has unsaved edits. */}
      <Slide in={pendingCount > 0} direction="up" mountOnEnter unmountOnExit>
        <Paper
          elevation={6}
          sx={{
            position: 'sticky',
            bottom: 16,
            mt: 2,
            mx: 'auto',
            maxWidth: 640,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
            bgcolor: 'background.paper',
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            boxShadow: `0 10px 30px ${alpha(theme.palette.common.black, 0.12)}`
          }}
        >
          <Box
            sx={{
              width: 32, height: 32, borderRadius: '50%',
              bgcolor: alpha(theme.palette.warning.main, 0.12),
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
          >
            <EditOutlined sx={{ fontSize: 18, color: 'warning.dark' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600}>
              {pendingCount} unsaved change{pendingCount === 1 ? '' : 's'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Edits are kept locally — click Save to apply.
            </Typography>
          </Box>
          <Button
            size="small"
            variant="text"
            color="inherit"
            startIcon={<Undo fontSize="small" />}
            onClick={handleDiscard}
            disabled={saving}
            sx={{ textTransform: 'none' }}
          >
            Discard
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save fontSize="small" />}
            onClick={handleSave}
            disabled={saving}
            sx={{ textTransform: 'none', borderRadius: 2, px: 2 }}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </Paper>
      </Slide>
    </Box>
  );
};

const Stat: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number;
  accent?: string;
}> = ({ icon, label, value, accent }) => (
  <Stack direction="row" spacing={1.25} alignItems="center">
    <Box sx={{ display: 'flex', alignItems: 'center' }}>{icon}</Box>
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} sx={{ color: accent ?? 'text.primary', lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  </Stack>
);

export default ClassTeachersTab;
